import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useTheme, useMediaQuery } from '@mui/material';
import { Alert, Avatar, Box, Button, Dialog, DialogContent, DialogTitle, FormControl, IconButton, Pagination, PaginationItem, Typography } from "@mui/material";
import { Link, useParams } from "react-router-dom";
import CircularProgress from '@mui/material/CircularProgress';
import { useWeb3ModalAccount, useWeb3Modal } from "@web3modal/ethers/react";
import HashLinkObserver from 'react-hash-link';
import LinkIcon from '@mui/icons-material/OpenInNewOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CopyIcon from '@mui/icons-material/ContentCopy';
import copy from 'copy-to-clipboard';
import { ethers, MaxUint256 } from 'ethers';
import { NumericFormat } from "react-number-format";
import PageBox from "../../components/layout/pageBox";
import TokenLogo from "../../components/tokenLogo";
import { priceFormatter } from "../../components/tvchart/chart";
import Toggle from "../../components/toggle";
import { Creator, User, UserName } from "../../components/cards/user";
import styled from 'styled-components';

import imgUniswap from '../../assets/images/uniswap.png';
import marketcapIcon from '../../assets/images/marketcap.png';
import TelegramIcon from '../../assets/images/telegram.svg';
import TwitterIcon from '../../assets/images/x.svg';
import WebsiteIcon from '../../assets/images/website.svg';
import ethIcon from '../../assets/images/coin/eth-1.png';

import { TVChartContainer as TVChartContainerAdvanced } from '../../components/tvchart';
import { TradeBox, SwapBox, CurrencyInput, SocialLink, HolderBox } from "./DetailsPageStyles";
import { TimeDiff } from "../../components/time";
// import { useContractInfo } from "../../hooks/contract";
import { useUserInfo } from "../../hooks/user";
import { useTokenInfo } from "../../hooks/token";
import { toast } from "react-toastify";
import { useChainInfo, useContractInfo, useSwitchChain } from "../../hooks/config";


const SlippageInput = styled.input<{ slippage: number }>`
  width: 48px;
  height: 24px;
  padding: 12px;
  font-size: 16px;
  border-radius: 16px;
  border: 2px solid ${({ slippage }) => (slippage !== 0.1 && slippage !== 0.5 && slippage !== 1 ? 'white' : '#333')};
  outline: none;
  background-color: transparent;
  color: white;
  transition: border-color 0.2s ease-in-out;
  text-align: center;

  &:focus {
    border-color: #6a5acd;
  }

  &::placeholder {
    color: #bbb;
  }
`;

const Divider = styled('hr')`
    width: 100%;
    margin: 0;
    padding: 0;
    border: none;
    border-bottom: 1px solid #27272A;
`

const Progress = styled.div<{ value: number }>`
    max-width: 400px;
    width: 100%;
    height: 10px;
    background: black;
    position: relative;
    transform: skewX(-33deg);
    &::after {
        content: "";
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: ${({ value }) => value}%;
        background: white;
    }
    @media(max-width: 800px) {
        min-width: 200px;
    }
`

const SmallButton = styled(Button)`
    &.MuiButton-root {
        background: #121212;
        border-radius: 4px;
        color: #C1C1C1;
        font-size: 12px;
        padding: 2px 8px;
        &:hover {
            background: #121212;
            color: #C1C1C1;
        }
    }
`

const TradeMenu = styled.div`
    position: fixed;
    z-index: 2;
    display: flex;
    left: 0;
    bottom: 0;
    right: 0;
    justify-content: space-around;
    gap: 8px;
    padding: 8px;
    background: #121212;
    & > button {
        flex: 1;
        padding: 12px 16px;
        background: rgba(255, 255, 255, 0.1);
        border: none;
        outline: none;
        border-radius: 8px;
        color: white;
        font-size: 16px;
        cursor: pointer;
    }
`

const StatsBox = styled.div`
    margin-top: 4px;
    display: flex;
    background: #212121;
    border-radius: 4px;
    padding: 4px 8px;
    gap: 4px;
`

const SwapContent = ({
    amountIn, approveHandler, detailData, error, estimateAmount, ethBalance, exactInput, isLoading, swapHandler, tokenAllowance, tokenBalance, tradeType,
    setAmountIn, setExactInput, setShowSlipaggeDialog
}: any) => {
    const { open: connect } = useWeb3Modal()
    const { chain, getChain } = useChainInfo()
    const { isConnected } = useWeb3ModalAccount()
    const { isSwitching, switchChain } = useSwitchChain()
    const tokenChain = getChain(detailData?.network)

    if (!detailData || !tokenChain)
        return <Box display="flex">
            <CircularProgress />
        </Box>

    return <>
        <Box display="flex" alignContent="center" justifyContent="space-between" width="100%">
            {tradeType === "buy" && (
                <SmallButton onClick={() => {
                    setExactInput(!exactInput);
                    setAmountIn(estimateAmount);
                }}>
                    Switch to {exactInput ? detailData.tokenSymbol : 'PLS'}
                </SmallButton>
            )}
            <SmallButton onClick={() => setShowSlipaggeDialog(true)}>
                Trade settings
            </SmallButton>
        </Box>
        <FormControl variant="standard" sx={{ my: 3 }}>
            <CurrencyInput>
                <Typography component="span" className="balance">
                    Balance: {isConnected ? priceFormatter(ethers.formatEther((tradeType === "buy" && exactInput ? ethBalance : tokenBalance) ?? 0n)) : '0'}
                </Typography>
                <Box display="flex" alignItems="center" gap="4px">
                    <Typography color="white">{exactInput && tradeType === "buy" ? 'ETH' : detailData.tokenSymbol.toUpperCase()}</Typography>
                    {(exactInput && tradeType === "buy") ? (
                        <Avatar src={ethIcon} sx={{ width: 20, height: 20 }} />
                    ) : (
                        <TokenLogo logo={detailData.tokenImage} size="20px" style={{ width: '20px', height: '20px', borderRadius: '20px' }} />
                    )}
                    <NumericFormat
                        placeholder="0.0"
                        thousandSeparator
                        valueIsNumericString
                        value={amountIn ?? ''}
                        onValueChange={(values) => {
                            console.log(values)
                            setAmountIn(values.value)
                        }}
                    />
                </Box>
            </CurrencyInput>
        </FormControl>
        {(exactInput || tradeType === "sell") && (
            <Box display="flex" gap="4px" flexWrap="wrap">
                {/* <Button className="medium small" onClick={() => setAmountIn(undefined)}>
                                <Avatar src={resetIcon} sx={{ width: 16, height: 16 }} />
                                Reset
                            </Button> */}
                {tradeType === "buy" && (
                    <>
                        <SmallButton onClick={() => setAmountIn('0.1')}>0.1 ETH</SmallButton>
                        <SmallButton onClick={() => setAmountIn('0.2')}>0.2 ETH</SmallButton>
                        <SmallButton onClick={() => setAmountIn('1')}>1 ETH</SmallButton>
                        <SmallButton onClick={() => setAmountIn(ethers.formatEther(ethBalance ?? '0'))}>MAX</SmallButton>
                    </>
                )}
                {tradeType === "sell" && (
                    <>
                        <SmallButton onClick={() => setAmountIn(tokenBalance ? ethers.formatEther(tokenBalance * 25n / 100n) : undefined)}>25%</SmallButton>
                        <SmallButton onClick={() => setAmountIn(tokenBalance ? ethers.formatEther(tokenBalance * 50n / 100n) : undefined)}>50%</SmallButton>
                        <SmallButton onClick={() => setAmountIn(tokenBalance ? ethers.formatEther(tokenBalance * 75n / 100n) : undefined)}>75%</SmallButton>
                        <SmallButton onClick={() => setAmountIn(ethers.formatEther(tokenBalance))}>100%</SmallButton>
                    </>
                )}
            </Box>
        )}
        {!!error && (
            <Alert severity="error" sx={{ borderRadius: '16px' }}>{error}</Alert>
        )}
        {
            isConnected
                ? tokenChain.chainId !== chain?.chainId
                    ? <Button fullWidth sx={{ borderRadius: '16px', fontSize: '20px', py: '12px', textTransform: 'none' }} disabled={isSwitching} onClick={() => switchChain(tokenChain?.chainId)}>
                        Switch Network {isSwitching && <CircularProgress size={18} style={{ color: "black", marginLeft: "1em" }} />}
                    </Button>
                    : tradeType === "sell" && amountIn && tokenAllowance < ethers.parseEther(amountIn)
                        ? <Button fullWidth sx={{ borderRadius: '16px', fontSize: '20px', py: '12px', textTransform: 'none' }} disabled={isLoading || !!error || !amountIn} onClick={approveHandler}>
                            Approve {isLoading && <CircularProgress size={18} style={{ color: "black", marginLeft: "1em" }} />}
                        </Button>
                        : <Button fullWidth sx={{ borderRadius: '16px', fontSize: '20px', py: '12px', textTransform: 'none' }} disabled={isLoading || !!error || !amountIn} onClick={swapHandler}>
                            {tradeType === "buy" ? "Buy" : "Sell"} {isLoading && <CircularProgress size={18} style={{ color: "black", marginLeft: "1em" }} />}
                        </Button>
                : <Button fullWidth sx={{ borderRadius: '16px', fontSize: '20px', py: '12px', textTransform: 'none' }} onClick={() => connect()}>
                    Connect Wallet
                </Button>
        }
        {!!estimateAmount && (
            <SmallButton>
                You will receive ~{priceFormatter(estimateAmount)} {exactInput && tradeType === "buy" ? detailData.tokenSymbol.toUpperCase() : 'ETH'}
            </SmallButton>
        )}
    </>
}

export default function DetailPage() {
    const { address } = useWeb3ModalAccount()
    const { chain: network, id } = useParams();
    // const [detailData, setDetailData] = React.useState<any>(null);
    // const [chatList, setChatList] = React.useState<any[]>([]);
    // const [tradeData, setTradeData] = React.useState<any>([]);
    // const [king, setKing] = React.useState<any>()
    // const [value, setValue] = React.useState('1');
    // const [holders, setHolders] = React.useState([]);
    const [amountIn, setAmountIn] = React.useState<string>();
    // const [type, setType] = React.useState("BUY");
    const [estimateAmount, setEstimateAmount] = React.useState<string>();
    const [modal, showModal] = React.useState<string>();
    // const [maxBuyPercent, setMaxBuyPercent] = React.useState(0n);
    // const [maxAmount, setMaxAmount] = React.useState('0');
    // const [launched, setLaunched] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [mode, setMode] = React.useState('trades');
    const [tradeType, setTradeType] = React.useState('buy');
    const [exactInput, setExactInput] = React.useState(true);
    // const [candleList, setCandleList] = React.useState<any[]>()
    // const [poolInfo, setPoolInfo] = React.useState<any>()
    const [errorCustom, setError] = React.useState<string>()
    const selectedDex = "Ethism"; // useMemo(() => dexList?.[0], [dexList])

    // const [marketCap, setMarketCap] = useState<number>()
    // const [now, setNow] = useState(0)
    const [pageOfTrades, setPageOfTrades] = useState(1)

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // const [signer, setSigner] = React.useState(null as any);
    // const [plsBalance, setPlsBalance] = React.useState(0n);
    // const [tokenBalance, setTokenBalance] = React.useState(0n);
    // const [tokenAllowance, setTokenAllowance] = React.useState(0n);
    const pageSize = isMobile ? 5 : 10
    const [showSlipaggeDialog, setShowSlipaggeDialog] = React.useState(false);
    const [slippage, setSlippage] = React.useState(0.1);

    const { chain, getChain } = useChainInfo()
    const { userInfo } = useUserInfo()
    const { ethismContract } = useContractInfo()
    const { tokenInfo, reload: reloadTokenInfo } = useTokenInfo(id as `0x${string}`, network as string, pageOfTrades, pageSize)

    const ethBalance = useMemo(() => userInfo?.balance ?? 0n, [userInfo])
    const { balance: tokenBalance, allowance: tokenAllowance, curveBalance: lpBalance } = useMemo(() => tokenInfo ?? { balance: 0n, allowance: 0n, curveBalance: 0n }, [tokenInfo])
    const poolInfo = useMemo(() => tokenInfo?.poolInfo, [tokenInfo])
    const {
        tokenContract,
        tokenDetils: detailData,
        trades: tradeData,
        tradesCount,
        holdersDetails: holders
    } = useMemo(() => tokenInfo ?? {}, [tokenInfo])

    const tokenChain = useMemo(() => network ? getChain(network) : undefined, [getChain, network])

    const marketCap = useMemo(() => Number(detailData?.marketcap ?? 0), [detailData])

    const pagesOfTrades = useMemo(() => tradesCount ? Math.floor(tradesCount / pageSize) + (tradesCount % pageSize === 0 ? 0 : 1) : 0, [pageSize, tradesCount])

    useEffect(() => {
        if (isMobile) {
            window.scrollTo(0, 0);
        }
    }, [isMobile]);

    useEffect(() => {
        const _slippage = localStorage.getItem('slippage');
        if (_slippage) {
            setSlippage(parseFloat(_slippage));
        }
    }, [slippage]);

    const error = useMemo(() => {
        return errorCustom
    }, [errorCustom])

    // useEffect(() => {
    //     if (isWrongChain)
    //         switchNetwork(DEFAULT_CHAIN_ID)
    // }, [isWrongChain, switchNetwork])

    const launched = useMemo(() => poolInfo?.launched || !!detailData?.launchedAt, [poolInfo, detailData])

    // const routerContract = useMemo(() => {
    //     if (selectedDex === undefined)
    //         return undefined
    //     // return new ethers.Contract(DEX_ROUTERS[selectedDex], routerABI, provider)
    //     return new ethers.Contract(DEFAULT_CONTRACT_ADDRESS, abiRouter, provider)
    // }, [selectedDex])

    useEffect(() => {
        try {
            if (poolInfo && amountIn) {
                if (launched) {
                    // if (selectedDex === undefined || !routerContract)
                    //     throw Error("Already launched!")
                    // if (tradeType === "buy") {
                    //     if (exactInput) {
                    //         const amountInWei = ethers.parseEther(amountIn)
                    //         if (amountInWei > ethBalance)
                    //             throw Error("Insufficient PLS balance")
                    //         routerContract.getAmountsOut(amountInWei, [WPLS, detailData.tokenAddress]).then(amountsOut => {
                    //             setEstimateAmount(ethers.formatEther(amountsOut[1]))
                    //         }).catch(() => { })
                    //     } else {
                    //         const amountOutWei = ethers.parseEther(amountIn)
                    //         routerContract.getAmountsIn(amountOutWei, [WPLS, detailData.tokenAddress]).then(amountsIn => {
                    //             if (amountsIn[0] > ethBalance)
                    //                 throw Error("Insufficient PLS balance")
                    //             setEstimateAmount(ethers.formatEther(amountsIn[0]))
                    //         }).catch(() => { })
                    //     }
                    // } else if (tradeType === "sell") {
                    //     const amountInWei = ethers.parseEther(amountIn)
                    //     if (amountInWei > tokenBalance)
                    //         throw Error(`Insufficient ${detailData?.tokenSymbol ?? 'Token'} balance.`)
                    //     routerContract.getAmountsOut(amountInWei, [detailData.tokenAddress, WPLS]).then(amountsOut => {
                    //         setEstimateAmount(ethers.formatEther(amountsOut[1]))
                    //     }).catch(() => { })
                    // }
                } else {
                    if (tradeType === "buy") {
                        if (exactInput) {
                            const amountInWei = ethers.parseEther(amountIn) * 97n / 100n
                            // if (amountInWei > poolInfo.virtualEthReserve * maxBuyPercent / 10000n)
                            //     throw Error(`Maximum: ${priceFormatter(ethers.formatEther(poolInfo.virtualEthReserve * maxBuyPercent / 10000n), 2)} PLS`)
                            if (amountInWei > ethBalance)
                                throw Error("Insufficient PLS balance")
                            setEstimateAmount(ethers.formatEther(amountInWei * poolInfo.virtualTokenReserve / (poolInfo.virtualEthReserve + amountInWei)))
                        } else {
                            const amountOutWei = ethers.parseEther(amountIn)
                            if (amountOutWei >= poolInfo.virtualTokenReserve)
                                throw Error(`Amount exceeds current reserves.`)
                            const amountInWei = amountOutWei * poolInfo.virtualEthReserve / (poolInfo.virtualTokenReserve - amountOutWei) + 1n
                            // if (estAmount * 97n / 100n > poolInfo.virtualEthReserve * maxBuyPercent / 10000n)
                            //     throw Error(`Maximum: ${priceFormatter(ethers.formatEther(poolInfo.virtualTokenReserve * maxBuyPercent / 10000n), 2)} ${detailData.tokenSymbol}`)
                            setEstimateAmount(ethers.formatEther(amountInWei * 100n / 97n))
                            if (amountInWei * 100n / 97n > ethBalance)
                                throw Error(`Insufficient PLS balance.`)
                        }
                    } else if (tradeType === "sell") {
                        const amountInWei = ethers.parseEther(amountIn)
                        // if (amountInWei > poolInfo.virtualTokenReserve)
                        //     throw Error("Amount exceeds current reserves.")
                        if (amountInWei > tokenBalance)
                            throw Error(`Insufficient ${detailData?.tokenSymbol ?? 'Token'} balance.`)
                        setEstimateAmount(ethers.formatEther(amountInWei * poolInfo.virtualEthReserve / (poolInfo.virtualTokenReserve + amountInWei) * 97n / 100n))
                    }
                }
            } else
                setEstimateAmount(undefined)
            // setError(undefined)
        } catch (err: any) {
            setEstimateAmount(undefined)
            // setError(err.message)
        }
    }, [amountIn, poolInfo, tradeType, exactInput, detailData, launched, selectedDex, ethBalance, tokenBalance])

    // const [estimateAmount, error] = useMemo(() => {
    //     try {
    //         if (poolInfo && amountIn) {
    //             if (launched) {
    //                 if (selectedDex === undefined || !routerContract)
    //                     throw Error("Already launched!")
    //                 if (tradeType === "buy") {
    //                     if (exactInput) {
    //                         const amountInWei = ethers.parseEther(amountIn)
    //                         if (amountInWei > ethBalance)
    //                             throw Error("Insufficient PLS balance")
    //                         routerContract.getAmountsOut(amountInWei, [WPLS, detailData.tokenAddress]).then(amountsOut => {
    //                             setEstimateAmount(ethers.formatEther(amountsOut[1]))
    //                         }).catch(() => { })
    //                     } else {
    //                         const amountOutWei = ethers.parseEther(amountIn)
    //                         routerContract.getAmountsIn(amountOutWei, [WPLS, detailData.tokenAddress]).then(amountsIn => {
    //                             if (amountsIn[0] > ethBalance)
    //                                 throw Error("Insufficient PLS balance")
    //                             setEstimateAmount(ethers.formatEther(amountsIn[0]))
    //                         }).catch(() => { })
    //                     }
    //                 } else if (tradeType === "sell") {
    //                     const amountInWei = ethers.parseEther(amountIn)
    //                     if (amountInWei > tokenBalance)
    //                         throw Error(`Insufficient ${detailData?.tokenSymbol ?? 'Token'} balance.`)
    //                     routerContract.getAmountsOut(amountInWei, [detailData.tokenAddress, WPLS]).then(amountsOut => {
    //                         setEstimateAmount(ethers.formatEther(amountsOut[1]))
    //                     }).catch(() => { })
    //                 }
    //             } else {
    //                 if (tradeType === "buy") {
    //                     if (exactInput) {
    //                         const amountInWei = ethers.parseEther(amountIn) * 97n / 100n
    //                         // if (amountInWei > poolInfo.virtualEthReserve * maxBuyPercent / 10000n)
    //                         //     throw Error(`Maximum: ${priceFormatter(ethers.formatEther(poolInfo.virtualEthReserve * maxBuyPercent / 10000n), 2)} PLS`)
    //                         if (amountInWei > ethBalance)
    //                             throw Error("Insufficient PLS balance")
    //                         setEstimateAmount(ethers.formatEther(amountInWei * poolInfo.virtualTokenReserve / (poolInfo.virtualEthReserve + amountInWei)))
    //                     } else {
    //                         const amountOutWei = ethers.parseEther(amountIn)
    //                         if (amountOutWei >= poolInfo.virtualTokenReserve)
    //                             throw Error(`Amount exceeds current reserves.`)
    //                         const amountInWei = amountOutWei * poolInfo.virtualEthReserve / (poolInfo.virtualTokenReserve - amountOutWei) + 1n
    //                         // if (estAmount * 97n / 100n > poolInfo.virtualEthReserve * maxBuyPercent / 10000n)
    //                         //     throw Error(`Maximum: ${priceFormatter(ethers.formatEther(poolInfo.virtualTokenReserve * maxBuyPercent / 10000n), 2)} ${detailData.tokenSymbol}`)
    //                         setEstimateAmount(ethers.formatEther(amountInWei * 100n / 97n))
    //                         if (amountInWei * 100n / 97n > ethBalance)
    //                             throw Error(`Insufficient PLS balance.`)
    //                     }
    //                 } else if (tradeType === "sell") {
    //                     const amountInWei = ethers.parseEther(amountIn)
    //                     // if (amountInWei > poolInfo.virtualTokenReserve)
    //                     //     throw Error("Amount exceeds current reserves.")
    //                     if (amountInWei > tokenBalance)
    //                         throw Error(`Insufficient ${detailData?.tokenSymbol ?? 'Token'} balance.`)
    //                     setEstimateAmount(ethers.formatEther(amountInWei * poolInfo.virtualEthReserve / (poolInfo.virtualTokenReserve + amountInWei) * 97n / 100n))
    //                 }
    //             }
    //         } else
    //             setEstimateAmount(undefined)
    //         setError(undefined)
    //     } catch (err: any) {
    //         setEstimateAmount(undefined)
    //         setError(err.message)
    //     }
    // }, [])

    const approveHandler = useCallback(async () => {
        setIsLoading(true);
        try {
            if (!detailData || tradeType !== "sell" || !amountIn)
                throw Error("");
            if (!address || !ethismContract)
                throw Error("Connect wallet");
            const approveAmount = MaxUint256
            // const tx = await tokenContract.approve(selectedDex ? DEX_ROUTERS[selectedDex] : DEFAULT_CONTRACT_ADDRESS, approveAmount)
            const tx = await tokenContract.approve(ethismContract.target, approveAmount)
            await tx.wait()
            toast.success("Successfully approved!");
            setIsLoading(false);
        } catch (error: any) {
            setIsLoading(false);
            const messageError = error?.shortMessage || error?.data?.message;
            toast.error(messageError);
        }
    }, [detailData, tradeType, amountIn, address, tokenContract, ethismContract])

    const showSuccessToast = useCallback((message: string) => {
        const link = `${chain?.explorerUrl}/tx/${message}`
        toast.success(
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span>Transaction success!</span>
                <a style={{ textDecoration: 'none', color: 'white' }} target="_blank" rel="noreferrer" href={link}>See tx in explorer <LinkIcon sx={{ fontSize: 14 }} /></a>
            </div>
        );
    }, [chain])

    const swapHandler = useCallback(async () => {
        let tx;
        setIsLoading(true);
        try {
            if (!detailData || !tokenContract || !amountIn)
                throw Error("");
            if (!address || !tokenContract || !ethismContract)
                throw Error("Connect wallet");
            // const balanceBefore = await tokenContract.balanceOf(signer.address)
            // const plsBefore = await provider.getBalance(signer.address)
            // const amountOutput = tradeType === "buy"
            //     ? ethers.parseEther(String(exactInput ? estimateAmount : amountIn))
            //     : ethers.parseEther(String(estimateAmount))
            // const stableUnit = await factoryContract.STABLE_UNIT()
            // const poolInfo = await factoryContract.tokenPools(id)
            // const poolInfo = await factoryContract.tokenPools(detailData.tokenAddress)
            // let receipt = undefined

            // const _slippage = 10n // 0.1%
            const _slippage = BigInt(Math.floor(slippage * 100));
            if (launched) {
                // if (selectedDex) {

                //     const deadline = Math.floor(Date.now() / 1000) + 180
                //     const routerContract = new ethers.Contract(DEX_ROUTERS[selectedDex], routerABI, signer)
                //     if (tradeType === "buy") {
                //         if (exactInput) {
                //             tx = await routerContract.swapExactETHForTokensSupportingFeeOnTransferTokens(
                //                 ethers.parseEther(String(estimateAmount)) * (10000n - _slippage) / 10000n,
                //                 [WPLS, detailData.tokenAddress],
                //                 address,
                //                 deadline,
                //                 { value: ethers.parseEther(amountIn) }
                //             )
                //             // await tx.wait()
                //         } else {
                //             tx = await routerContract.swapETHForExactTokens(
                //                 ethers.parseEther(amountIn),
                //                 [WPLS, detailData.tokenAddress],
                //                 address,
                //                 deadline,
                //                 { value: ethers.parseEther(String(estimateAmount)) * (10000n + _slippage) / 10000n }
                //             )
                //             // await tx.wait()
                //         }
                //     } else {
                //         tx = await routerContract.swapExactTokensForETHSupportingFeeOnTransferTokens(
                //             ethers.parseEther(amountIn),
                //             ethers.parseEther(String(estimateAmount)) * (10000n - _slippage) / 10000n,
                //             [detailData.tokenAddress, WPLS],
                //             address,
                //             deadline
                //         )
                //         // await tx.wait()
                //     }
                // }
            } else {
                // const amountInput = tradeType === "buy"
                //     ? ethers.parseEther((exactInput ? amountIn : estimateAmount) ?? '0')
                //     : ethers.parseEther(amountIn)
                if (tradeType === "buy") {
                    const firstBuyFee = await ethismContract.getFirstBuyFee(detailData.tokenAddress)
                    if (exactInput) {
                        const amountInput = ethers.parseEther(amountIn ?? '0')
                        const amountOutMin = ethers.parseEther(estimateAmount ?? '0') * (10000n - _slippage) / 10000n
                        const gas = await ethismContract.swapExactETHForTokens.estimateGas(detailData.tokenAddress, amountInput, amountOutMin, { value: amountInput + firstBuyFee })
                        tx = await ethismContract.swapExactETHForTokens(detailData.tokenAddress, amountInput, amountOutMin, { value: amountInput + firstBuyFee, gas })
                        // await tx.wait()
                    } else {
                        const amountOut = ethers.parseEther(amountIn ?? '0')
                        const amountInMax = ethers.parseEther(estimateAmount ?? '0') * (10000n + _slippage) / 10000n
                        tx = await ethismContract.swapETHForExactTokens(detailData.tokenAddress, amountOut, amountInMax, { value: amountInMax + firstBuyFee })
                        // await tx.wait()
                    }
                } else {
                    const amountInput = ethers.parseEther(amountIn)
                    const amountOutMin = ethers.parseEther(estimateAmount ?? '0') * (10000n - _slippage) / 10000n
                    const allowance = await tokenContract.allowance(address, ethismContract.target)
                    if (allowance < amountInput) {
                        const approveAmount = MaxUint256
                        const tx = await tokenContract.approve(ethismContract.target, approveAmount)
                        await tx?.wait()
                    }
                    tx = await ethismContract.swapExactTokensForETH(detailData.tokenAddress, amountInput, amountOutMin)
                    // await tx.wait()
                }
            }
            // const logTrade = receipt.logs.find((log: any) => (tradeType==="buy" && log.eventName==="BuyTokens") || (tradeType==="sell" && log.eventName==="SellTokens"))
            // const logLaunch = receipt.logs.find((log: any) => tradeType==="buy" && log.eventName==="LiquidityAdded")
            // const { launched } = await factoryContract.tokenPools(id)
            // const balanceAfter = await tokenContract.balanceOf(signer.address)
            // const tokenPrice = await factoryContract.getPrice(detailData.tokenAddress)
            // const marketcap = await factoryContract.getTokenMarketCap(detailData.tokenAddress)
            // const ethPrice = await factoryContract.getETHPriceByUSD()
            // const tokenPriceUSD = ethers.formatEther(BigInt(tokenPrice) * BigInt(ethPrice) / stableUnit)
            // const body = {
            //     type: "swap",
            //     data: {
            //         tokenName: detailData.tokenName,
            //         tokenSymbol: detailData.tokenSymbol,
            //         tokenAddress: detailData.tokenAddress,
            //         tokenImage: detailData.tokenImage,
            //         creatorAddress: detailData.creatorAddress,
            //         swapperAddress: signer.address,
            //         type: tradeType.toUpperCase(),
            //         ethAmount: ethers.formatEther(logTrade.args.plsAmount), // ethers.formatEther(tradeType === "buy" ? amountInput : amountOutput),
            //         tokenAmount: ethers.formatEther(logTrade.args.tokenAmount), // ethers.formatEther(tradeType === "sell" ? amountInput : amountOutput),
            //         launched: !!logLaunch,
            //         network: chain,
            //         marketcap: String(marketcap),
            //         txHash: receipt.hash,
            //         tokenPrice: tokenPriceUSD,
            //         tokenBalanceOfUser: ethers.formatEther(balanceAfter)
            //     }
            // }
            setAmountIn(undefined)
            // setTokenBalance(Number(ethers.formatEther(balanceAfter)))
            // socket.emit("message", JSON.stringify(body))
            // toast.success("Transaction success");

            showSuccessToast(tx.hash)
            setIsLoading(false);
            reloadTokenInfo()
            // getTrades().then(() => { })
            // getChartData(activePeriod)
        } catch (error: any) {
            setIsLoading(false);
            console.log({
                error
            });
            const messageError = error?.shortMessage || error?.data?.message;
            toast.error(messageError);
        }
    }, [detailData, tokenContract, amountIn, address, ethismContract, slippage, launched, showSuccessToast, reloadTokenInfo, tradeType, exactInput, estimateAmount])

    const parseLink = (domain: string, link?: string) => {
        if (!link)
            return undefined
        if (domain) {
            if (link.startsWith(`https://${domain}/`))
                return link
            if (link.startsWith(`${domain}/`) || link.startsWith(`/${domain}/`))
                return `https://${link}`
            return `https://${domain}/${link}`
        } else {
            if (link.startsWith(`https://`))
                return link
            return `https://${link}`
        }
    }

    const telegramLink = useMemo(() => {
        return parseLink('t.me', detailData?.telegramLink)
    }, [detailData])

    const twitterLink = useMemo(() => {
        return parseLink('x.com', detailData?.twitterLink)
    }, [detailData])

    const webLink = useMemo(() => {
        return parseLink('', detailData?.webLink)
    }, [detailData])

    // const dexList: DexKey[] | undefined = useMemo(() => {
    //     if (detailData && detailData.pairAddresses)
    //         return Object.keys(detailData.pairAddresses) as DexKey[]
    //     if (poolInfo?.bitmapRouters) {
    //         const routers: DexKey[] = []
    //         const ROUTERS: DexKey[] = ["9INCH", "9mm", "PulseX"]
    //         for (const i in ROUTERS) {
    //             if ((Number(poolInfo.bitmapRouters) >> Number(i)) & 0x1)
    //                 routers.push(ROUTERS[i])
    //         }
    //         if (routers.length)
    //             return routers
    //     }
    //     return undefined
    // }, [detailData, poolInfo])

    return (
        <PageBox display="flex" flexDirection="column" justifyContent="space-between" gap="1.5em" maxWidth="100%" overflow="hidden">
            <HashLinkObserver smoothScroll />
            {/* {!!detailData?.tokenBanner && (
                <Banner src={`${IPFS_GATEWAY_URL}/${detailData.tokenBanner}`} />
            )} */}
            <div style={{ flex: 1, position: 'relative' }}>
                <Box display="flex" flexDirection="column" justifyContent="space-between" gap="0.5em">
                    <Box display="flex" gap="8px" alignItems="center">
                        <img src={`/networks/${detailData?.network}.svg`} height={isMobile ? 18 : 24} alt="" />
                        <Typography fontSize={{ md: 24, sm: 18, xs: 16 }} fontFamily="Arial" fontWeight="bold" color="white">{detailData?.tokenName}</Typography>
                        <Typography fontSize={{ md: 24, sm: 18, xs: 16 }} fontFamily="Arial" fontWeight="bold" color="#AAA">({detailData?.tokenSymbol})</Typography>
                        <Typography fontSize={12} color="#AAA" ml={{ md: "1em", sm: "auto", xs: "auto" }}>{detailData?.tokenAddress?.slice(0, 6)}...{detailData?.tokenAddress?.slice(-4)}</Typography>
                        <IconButton sx={{ width: 'fit-content' }} onClick={() => copy(detailData?.tokenAddress)}>
                            <CopyIcon sx={{ color: "#9E9E9E", width: 14, height: 14 }} />
                        </IconButton>
                    </Box>
                    <Box display="flex" gap="8px" alignItems="center" width="100%">
                        <TokenLogo logo={detailData?.tokenImage} size={isMobile ? "75px" : "120px"} />
                        {
                            !isMobile &&
                            <Box>
                                <Box display="flex" gap="8px" alignItems="baseline" position="relative" width="fit-content">
                                    <Typography fontSize={48} fontFamily="Arial" fontWeight="bold" color="white">${priceFormatter(marketCap, 2)}</Typography>
                                    <Typography fontSize={12} color="white">Market cap</Typography>
                                    <Box 
                                        position="absolute" 
                                        px="8px" right={0} top={12} 
                                        display="flex" justifyContent="center" alignItems="center" 
                                        bgcolor="white" borderRadius="10px"
                                        // title={`When marcket cap reaches at $${priceFormatter(tokenChain?.targetMarketCap, 2)}, all the liquidity will be listed on Uniswap`}
                                    >
                                        <Avatar src={imgUniswap} sx={{ width: 20, height: 20 }} />
                                        <Typography fontSize={14} color="#ff1383" fontWeight="bold">v{detailData?.poolType + 1}</Typography>
                                    </Box>
                                </Box>
                                <Box display="flex" gap="8px" alignItems="baseline">
                                    <Typography fontSize={14} color={Number(detailData?.price15m ?? 0) >= 0 ? "lightgreen" : "darkorange"}>
                                        {Number(detailData?.price15m ?? 0) >= 0 ? '+' : '-'}${priceFormatter(Math.abs(Number(detailData?.price15m ?? 0)))} ({Number(detailData?.priceChange15m).toFixed(2)}%)
                                    </Typography>
                                    <Typography fontSize={12} color="white">Past 15m</Typography>
                                </Box>
                                <Box display="flex" gap="8px">
                                    <StatsBox>
                                        <Typography fontSize={12} color="#9E9E9E">Virtual Liquidity:</Typography>
                                        <Typography fontSize={12} color="white">${priceFormatter(detailData?.liquidity ?? 0, 2, true, true)}</Typography>
                                    </StatsBox>
                                    <StatsBox>
                                        <Typography fontSize={12} color="#9E9E9E">Volume:</Typography>
                                        <Typography fontSize={12} color="white">${priceFormatter(detailData?.volume ?? 0, 2, true, true)}</Typography>
                                    </StatsBox>
                                    {
                                        !!detailData?.creationTime &&
                                        <StatsBox>
                                            <Typography fontSize={12} color="#9E9E9E">Created At:</Typography>
                                            <Typography fontSize={12} color="white">
                                                {
                                                    Date.now() - new Date(detailData.creationTime).getTime() < 3600000
                                                        ? <TimeDiff time={new Date(detailData.creationTime)} postfix="ago" />
                                                        : new Date(detailData.creationTime).toLocaleString('en-US', { month: 'short', year: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
                                                }
                                            </Typography>
                                        </StatsBox>
                                    }
                                </Box>
                            </Box>
                        }
                        <Box alignSelf={{ md: "flex-end", sm: "flex-start", xs: "flex-start" }} flex={1} ml="auto" display="flex" flexDirection="column" alignItems="flex-end" gap={{ xs: '4px', sm: '4px', md: '8px' }}>
                            {
                                !launched &&
                                <>
                                    <Typography fontSize={{ xs: 10, sm: 10, md: 14 }} color="#DDD">Progress ({Math.min(99.99, Number(detailData?.progress ?? 0)).toFixed(2)}%)</Typography>
                                    <Progress value={Math.min(100, Number(detailData?.progress ?? 0))} />
                                    <Box display="flex" alignItems="center" gap="8px">
                                        <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M5.05762 13.3333H11.7243" stroke="#C1C1C1" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M7.05762 13.3333C10.7243 11.6667 7.59095 9.06666 9.05762 6.66666" stroke="#C1C1C1" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M6.72428 6.26668C7.45762 6.80001 7.92428 7.73334 8.25762 8.73334C6.92428 9.00001 5.92428 9.00001 5.05762 8.53334C4.25762 8.13334 3.52428 7.26668 3.05762 5.73334C4.92428 5.40001 5.99095 5.73334 6.72428 6.26668Z" stroke="#C1C1C1" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M9.79097 3.99999C9.28269 4.79436 9.02702 5.72409 9.05764 6.66666C10.3243 6.59999 11.2576 6.26666 11.9243 5.73332C12.591 5.06666 12.991 4.19999 13.0576 2.66666C11.2576 2.73332 10.391 3.33332 9.79097 3.99999Z" stroke="#C1C1C1" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        <Typography fontSize={{ xs: 10, sm: 10, md: 14 }} color="#DDD">1d liquidity: {priceFormatter(detailData?.liquidity1d ?? 0)} ETH</Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center" justifyContent="center" gap="8px" mt={1}>
                                        {!!webLink && (
                                            <SocialLink to={webLink} target="_blank">
                                                <img src={WebsiteIcon} width={12} height={12} alt="website" />
                                                {!isMobile && 'Website'}
                                            </SocialLink>
                                        )}
                                        {!!twitterLink && (
                                            <SocialLink to={twitterLink} target="_blank">
                                                <img src={TwitterIcon} width={12} height={12} alt="twitter" />
                                                {!isMobile && 'Twitter'}
                                            </SocialLink>
                                        )}
                                        {!!telegramLink && (
                                            <SocialLink to={telegramLink} target="_blank">
                                                <img src={TelegramIcon} width={12} height={12} alt="telegram" />
                                                {!isMobile && 'Telegram'}
                                            </SocialLink>
                                        )}
                                    </Box>
                                </>
                            }
                        </Box>
                    </Box>
                </Box>
                {
                    isMobile &&
                    <Box mt={1}>
                        <Box display="flex" gap="8px" alignItems="baseline">
                            <Typography fontSize={32} fontFamily="Arial" fontWeight="bold" color="white">${priceFormatter(marketCap, 2)}</Typography>
                            <Typography fontSize={12} color="white">Market cap</Typography>
                        </Box>
                        <Box display="flex" gap="8px" alignItems="baseline">
                            <Typography fontSize={14} color="lightgreen">
                                {Number(detailData?.price ?? 0) >= Number(detailData?.price15m ?? 0) ? '+' : '-'}${priceFormatter(Number(detailData?.price15m ?? 0) - Number(detailData?.price15MinAgo ?? 0))} ({Number(detailData?.priceChange15m).toFixed(2)}%)
                            </Typography>
                            <Typography fontSize={12} color="white">Past 15m</Typography>
                        </Box>
                        <Box display="flex" flexDirection="column" alignItems="stretch" gap="1px">
                            <StatsBox>
                                <Typography fontSize={12} color="#9E9E9E">Virtual Liquidity:</Typography>
                                <Typography fontSize={12} color="white">${priceFormatter(detailData?.liquidity ?? 0, 2, true, true)}</Typography>
                            </StatsBox>
                            <StatsBox>
                                <Typography fontSize={12} color="#9E9E9E">Volume:</Typography>
                                <Typography fontSize={12} color="white">${priceFormatter(detailData?.volume ?? 0, 2, true, true)}</Typography>
                            </StatsBox>
                            {
                                !!detailData?.creationTime &&
                                <StatsBox>
                                    <Typography fontSize={12} color="#9E9E9E">Created At:</Typography>
                                    <Typography fontSize={12} color="white">
                                        {
                                            Date.now() - new Date(detailData.creationTime).getTime() < 3600000
                                                ? <TimeDiff time={new Date(detailData.creationTime)} postfix="ago" />
                                                : new Date(detailData.creationTime).toLocaleString('en-US', { month: 'short', year: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
                                        }
                                    </Typography>
                                </StatsBox>
                            }
                            <StatsBox>
                                <Typography fontSize={12} color="#9E9E9E">Will be deposited to:</Typography>
                                <Typography fontSize={12} color="white">Uniswap V{detailData?.poolType + 1} pool</Typography>
                            </StatsBox>
                        </Box>
                    </Box>
                }
                <Box display="flex" justifyContent="space-between" gap="2em" flexDirection={{ md: 'row', sm: 'column', xs: 'column' }} mt={2} mb={4}>
                    <Box flex="1" display="flex" flexDirection="column">
                        {/* {
                            launched &&
                            <Box display="flex" gap="1em" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" mb="2em" alignItems={{ sm: "flex-end", xs: "stretch" }}>
                                <Toggle style={{ width: "auto" }}>
                                    <div className={selectedDex === undefined ? "active" : ""} onClick={() => selectDex(undefined)}>CoinQuest</div>
                                    {
                                        dexList?.map((dex: DexKey) =>
                                            <div className={selectedDex === dex ? "active" : ""} onClick={() => selectDex(dex)}>{dex}</div>
                                        )
                                    }
                                </Toggle>
                                {
                                    !!selectedDex &&
                                    <Link to={`${DEX_LINKS[selectedDex]}${id}`} target="_blank" style={{ textDecoration: 'none' }}>
                                        <Button variant="text" color="warning" sx={{ borderColor: 'transparent', display: "flex", alignItems: "center", justifyContent: "center", height: 'fit-content', width: '100%' }}>
                                            Trade {detailData?.tokenSymbol} on {selectedDex}
                                            <LinkIcon sx={{ color: "#e19428", height: 16 }} />
                                        </Button>
                                    </Link>
                                }
                            </Box>
                        } */}
                        <TVChartContainerAdvanced token={id} network={detailData?.network} dex={selectedDex} />
                        <Box mt="2em">
                            <Box display="flex" justifyContent="space-between" flexDirection={{ xs: 'column', sm: 'row' }}>
                                <Toggle innerRadius>
                                    <div className={mode === "trades" ? "active" : ""} onClick={() => setMode("trades")}>Trades</div>
                                    <div className={mode === "info" ? "active" : ""} onClick={() => setMode("info")}>Info</div>
                                    {
                                        isMobile &&
                                        <div className={mode === "holders" ? "active" : ""} onClick={() => setMode("holders")}>Holders</div>
                                    }
                                </Toggle>
                            </Box>
                            {mode === "trades" && (
                                <Box mb="1.5em">
                                    <Box display="flex" flexDirection="column" my="1.5em" borderRadius="12px" overflow="hidden" bgcolor="#191919" border="1px solid #27272A">
                                        <TradeBox sx={{ pt: 3, pb: 2 }}>
                                            <Typography color="white" fontSize={14} fontWeight="bold" display={{ sm: 'block', xs: 'none' }}>Date</Typography>
                                            <Typography color="white" fontSize={14} fontWeight="bold">Type</Typography>
                                            <Typography color="white" fontSize={14} fontWeight="bold">ETH</Typography>
                                            <Typography color="white" fontSize={14} fontWeight="bold">{detailData?.tokenSymbol}</Typography>
                                            <Typography color="white" fontSize={14} fontWeight="bold" display={{ sm: 'block', xs: 'none' }}>Trader</Typography>
                                            <Typography color="white" fontSize={14} fontWeight="bold">Transaction</Typography>
                                        </TradeBox>
                                        {
                                            tradeData?.map((trade: any) => (
                                                <Fragment key={trade.txHash}>
                                                    <Divider />
                                                    <TradeBox sx={{ py: 2, color: trade.type === "BUY" ? "lightgreen" : "#ef5350" }}>
                                                        <Typography color="inherit" fontSize="small" display={{ sm: 'block', xs: 'none' }}>
                                                            {
                                                                Date.now() - new Date(trade.date * 1000).getTime() < 3600000
                                                                    ? <TimeDiff time={new Date(trade.date * 1000)} postfix="ago" />
                                                                    : new Date(trade.date * 1000).toLocaleString('en-US', { month: 'short', year: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
                                                            }
                                                        </Typography>
                                                        <Typography color="inherit" fontSize="small">{trade.type}</Typography>
                                                        <Typography color="inherit" fontSize="small">{priceFormatter(trade.ethAmount, 2, true, true)}</Typography>
                                                        {/* <Typography color="inherit" fontSize="small">{priceFormatter(trade.tokenAmount * trade.tokenPrice, 2, true, true)}</Typography> */}
                                                        <Typography color="inherit" fontSize="small">{priceFormatter(trade.tokenAmount, 2, true, true)}</Typography>
                                                        <Box display={{ sm: 'flex', xs: 'none' }} alignItems="center">
                                                            <UserName user={trade.user} address={trade.swapperAddress} color={trade.type === "BUY" ? "lightgreen" : "#ef5350"} size={18} />
                                                        </Box>
                                                        <Box display={{ sm: 'block', xs: 'none' }}>
                                                            <Link style={{ textDecoration: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} to={`${tokenChain?.explorerUrl}/tx/${trade.txHash}`} target="_blank">
                                                                <Typography color={trade.type === "BUY" ? "lightgreen" : "#ef5350"} fontSize="small">{trade.txHash.slice(0, 5)}...{trade.txHash.slice(-6)}</Typography>
                                                                <LinkIcon sx={{ color: trade.type === "BUY" ? "lightgreen" : "#ef5350", fontSize: 14 }} />
                                                            </Link>
                                                        </Box>
                                                    </TradeBox>
                                                </Fragment>
                                            ))
                                        }
                                    </Box>
                                    <Pagination
                                        style={{ margin: 'auto', width: 'fit-content' }}
                                        variant="text"
                                        shape="circular"
                                        count={pagesOfTrades}
                                        page={pageOfTrades}  // current page
                                        onChange={(e, page) => setPageOfTrades(page)}  // handle page change
                                        renderItem={(data) => (
                                            <PaginationItem
                                                slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
                                                {...data}
                                            />
                                        )}
                                    />
                                </Box>
                            )}
                            {
                                mode === "info" &&
                                <Box bgcolor="#212121" borderRadius="16px" display="flex" gap="16px" padding="24px" my="1.5em">
                                    <TokenLogo logo={detailData?.tokenImage} size="120px" />
                                    <Box>
                                        <Box display="flex" gap="0.2rem">
                                            <Typography fontSize={24} color="white">{detailData?.tokenName}</Typography>
                                        </Box>
                                        <Box display="flex" gap="0.2rem" alignItems="center">
                                            <Typography color="#D9D9D9" fontSize={14}>{detailData?.tokenDescription}</Typography>
                                        </Box>
                                        <Box display="flex" gap="0.2rem" mt={1}>
                                            <Creator token={detailData} />
                                            <Typography color="#9E9E9E" fontSize={14}>—</Typography>
                                            <Typography color="#9E9E9E" fontSize={14}>creator</Typography>
                                        </Box>
                                        {/* <Box display="flex" gap="0.2rem">
                            <Avatar sx={{ width: 24, height: 24, mr: '0.5rem' }} src={repliesIcon} />
                            <Typography color="#D9D9D9" fontSize={14}>{detailData?.replies}</Typography>
                            <Typography color="#9E9E9E" fontSize={14}>replies</Typography>
                        </Box> */}
                                        <Box display="flex" gap="0.2rem" mt={1}>
                                            <Avatar sx={{ width: 24, height: 24, mr: '0.5rem' }} src={marketcapIcon} />
                                            <Typography color="#D9D9D9" fontSize={14}>${priceFormatter(marketCap, 2)}</Typography>
                                            <Typography color="#9E9E9E" fontSize={14}>—</Typography>
                                            <Typography color="#9E9E9E" fontSize={14}>Market cap</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            }
                            {
                                mode === "holders" &&
                                <HolderBox component="ol" mb="1.5em">
                                    <Box component="h3">Top Holders</Box>
                                    <Box component="li">
                                        <Typography>1.</Typography>
                                        {/* {
                                    isMobile
                                        ? <UserAvatar address={PULSECHAIN_CONTRACT_ADDRESS} color="#9E9E9E" user={{
                                            username: "Remaining supply", avatar: "bondingCurv"
                                        }} />
                                        : <Box display="flex" alignItems="center">
                                            <User address={PULSECHAIN_CONTRACT_ADDRESS} color="#9E9E9E" user={{
                                                username: "Remaining supply", avatar: "bondingCurv"
                                            }} />
                                        </Box>
                                } */}


                                        <Box display="flex" alignItems="center" flexGrow={1} minWidth={0}>
                                            <UserName
                                                address={tokenChain?.contractAddress}
                                                color="#9E9E9E"
                                                user={{
                                                    username: "Bonding Curve",
                                                    avatar: "bondingCurv"
                                                }}
                                            />
                                        </Box>

                                        <Box display="flex" alignItems="center" ml={1}>
                                            <Link to={`${tokenChain?.explorerUrl}/address/${tokenChain?.contractAddress}`} target="_blank" style={{ textDecoration: 'none', marginRight: '8px' }}>
                                                <LinkIcon sx={{ color: "white", height: 16 }} />
                                            </Link>
                                            <Typography noWrap>{ tokenChain?.totalSupply ? priceFormatter(Number(ethers.formatEther(lpBalance ?? 0n)) / tokenChain.totalSupply * 100, 2) : 0} %</Typography>
                                        </Box>
                                    </Box>
                                    {
                                        holders?.length > 0 && holders.sort((a: any, b: any) => b.tokenAmount - a.tokenAmount).map((item: any, index: number) => (
                                            <Box component="li" key={index}>
                                                <Typography>{index + 2}.</Typography>
                                                {/* {
                                        isMobile
                                        ? <UserAvatar user={item.user} address={item.holderAddress} postfix={item.holderAddress === item.creatorAddress ? " (Creator) " : ""} color="#9E9E9E" />
                                        : <Box display="flex" alignItems="center">
                                            <User user={item.user} address={item.holderAddress} postfix={item.holderAddress === item.creatorAddress ? " (Creator) " : ""} color="#9E9E9E" />
                                        </Box>
                                    } */}

                                                {
                                                    <Box display="flex" alignItems="center">
                                                        <UserName user={item.user} address={item.holderAddress} postfix={item.holderAddress === item.creatorAddress ? " (Creator) " : ""} color="#9E9E9E" />
                                                    </Box>
                                                }
                                                <Link to={`${tokenChain?.explorerUrl}/address/${item.holderAddress}`} target="_blank" style={{ textDecoration: 'none' }}>
                                                    <LinkIcon sx={{ color: "white", height: 16 }} />
                                                </Link>
                                                <Typography>{ tokenChain?.totalSupply ? priceFormatter(item.tokenAmount / tokenChain.totalSupply * 100, 2) : 0} %</Typography>
                                            </Box>
                                        ))}
                                </HolderBox>
                            }
                        </Box>
                    </Box>

                    {
                        !isMobile &&
                        <Box width="370px">
                            <SwapBox className={isLoading || launched ? "disabled" : ""}>
                                <Toggle style={{ width: "inherit" }}>
                                    <div className={tradeType === "buy" ? "active" : ""} onClick={() => setTradeType("buy")}>Buy</div>
                                    <div className={tradeType === "sell" ? "active" : ""} onClick={() => setTradeType("sell")}>Sell</div>
                                </Toggle>
                                <SwapContent
                                    amountIn={amountIn}
                                    approveHandler={approveHandler}
                                    detailData={detailData}
                                    error={error}
                                    estimateAmount={estimateAmount}
                                    ethBalance={ethBalance}
                                    exactInput={exactInput}
                                    isLoading={isLoading}
                                    swapHandler={swapHandler}
                                    tokenAllowance={tokenAllowance}
                                    tokenBalance={tokenBalance}
                                    tradeType={tradeType}
                                    setAmountIn={setAmountIn}
                                    setExactInput={setExactInput}
                                    setShowSlipaggeDialog={setShowSlipaggeDialog}
                                />
                            </SwapBox>
                            <HolderBox component="ol" mb="1.5em">
                                <Box component="h3">Top Holders</Box>
                                <Box component="li">
                                    <Typography>1.</Typography>
                                    {/* {
                                            isMobile
                                                ? <UserAvatar address={PULSECHAIN_CONTRACT_ADDRESS} color="#9E9E9E" user={{
                                                    username: "Remaining supply", avatar: "bondingCurv"
                                                }} />
                                                : <Box display="flex" alignItems="center">
                                                    <User address={PULSECHAIN_CONTRACT_ADDRESS} color="#9E9E9E" user={{
                                                        username: "Remaining supply", avatar: "bondingCurv"
                                                    }} />
                                                </Box>
                                        } */}


                                    <Box display="flex" alignItems="center" flexGrow={1} minWidth={0}>
                                        <User
                                            address={tokenChain?.contractAddress}
                                            color="#9E9E9E"
                                            user={{
                                                username: "Bonding Curve",
                                                avatar: "bondingCurv"
                                            }}
                                        />
                                    </Box>

                                    <Box display="flex" alignItems="center" ml={1}>
                                        <Link to={`${tokenChain?.explorerUrl}/address/${tokenChain?.contractAddress}`} target="_blank" style={{ textDecoration: 'none', marginRight: '8px' }}>
                                            <LinkIcon sx={{ color: "white", height: 16 }} />
                                        </Link>
                                        <Typography noWrap>{ tokenChain?.totalSupply ? priceFormatter(Number(ethers.formatEther(lpBalance ?? 0n)) / tokenChain.totalSupply * 100, 2) : 0 } %</Typography>
                                    </Box>
                                </Box>
                                {
                                    holders?.length > 0 && holders.sort((a: any, b: any) => b.tokenAmount - a.tokenAmount).map((item: any, index: number) => (
                                        <Box component="li" key={index}>
                                            <Typography>{index + 2}.</Typography>
                                            {/* {
                                                isMobile
                                                ? <UserAvatar user={item.user} address={item.holderAddress} postfix={item.holderAddress === item.creatorAddress ? " (Creator) " : ""} color="#9E9E9E" />
                                                : <Box display="flex" alignItems="center">
                                                    <User user={item.user} address={item.holderAddress} postfix={item.holderAddress === item.creatorAddress ? " (Creator) " : ""} color="#9E9E9E" />
                                                </Box>
                                            } */}

                                            {
                                                <Box display="flex" alignItems="center">
                                                    <UserName user={item.user} address={item.holderAddress} postfix={item.holderAddress === item.creatorAddress ? " (Creator) " : ""} color="#9E9E9E" />
                                                </Box>
                                            }
                                            <Link to={`${tokenChain?.explorerUrl}/address/${item.holderAddress}`} target="_blank" style={{ textDecoration: 'none' }}>
                                                <LinkIcon sx={{ color: "white", height: 16 }} />
                                            </Link>
                                            <Typography>{ tokenChain?.totalSupply ? priceFormatter(item.tokenAmount / tokenChain.totalSupply * 100, 2) : 0 } %</Typography>
                                        </Box>
                                    ))}
                            </HolderBox>
                        </Box>
                    }
                </Box>
            </div>
            {
                isMobile &&
                <TradeMenu>
                    <button onClick={() => {
                        setTradeType("buy")
                        showModal('trade')
                    }}>Buy</button>
                    <button onClick={() => {
                        setTradeType("sell")
                        showModal('trade')
                    }}>Sell</button>
                </TradeMenu>
            }

            <Dialog open={modal === "trade"} fullWidth keepMounted onClose={isLoading ? undefined : () => showModal(undefined)}>
                <DialogTitle textTransform="uppercase">
                    {tradeType}
                </DialogTitle>
                <DialogContent>
                    <SwapContent
                        amountIn={amountIn}
                        approveHandler={approveHandler}
                        detailData={detailData}
                        error={error}
                        estimateAmount={estimateAmount}
                        ethBalance={ethBalance}
                        exactInput={exactInput}
                        isLoading={isLoading}
                        swapHandler={swapHandler}
                        tokenAllowance={tokenAllowance}
                        tokenBalance={tokenBalance}
                        tradeType={tradeType}
                        setAmountIn={setAmountIn}
                        setExactInput={setExactInput}
                        setShowSlipaggeDialog={setShowSlipaggeDialog}
                    />
                </DialogContent>
            </Dialog>
            <Dialog
                open={showSlipaggeDialog}
                // TransitionComponent={Transition}
                keepMounted
                onClose={() => setShowSlipaggeDialog(false)}
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="center" alignItems="center" width="100%">
                        <Typography color="#FFFFFF" fontSize="24px">
                            Slippage
                        </Typography>
                        <IconButton onClick={() => setShowSlipaggeDialog(false)} sx={{ position: 'absolute', right: 8 }}>
                            <CloseIcon sx={{ color: "#9E9E9E" }} />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="row" alignContent="center" alignItems="center" justifyContent="center" gap={2}>
                        <Button
                            variant="contained"
                            onClick={() => {
                                setSlippage(0.1);
                                localStorage.setItem('slippage', '0.1');
                            }}
                            sx={{ borderColor: slippage === 0.1 ? 'white' : 'transparent', borderWidth: 2, borderStyle: 'solid' }}
                        >
                            0.1%
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                setSlippage(0.5)
                                localStorage.setItem('slippage', '0.5');
                            }}
                            sx={{ borderColor: slippage === 0.5 ? 'white' : 'transparent', borderWidth: 2, borderStyle: 'solid' }}
                        >
                            0.5%
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                setSlippage(1)
                                localStorage.setItem('slippage', '1');
                            }}
                            sx={{ borderColor: slippage === 1 ? 'white' : 'transparent', borderWidth: 2, borderStyle: 'solid' }}
                        >
                            1%
                        </Button>
                        <SlippageInput
                            slippage={slippage}
                            value={slippage}
                            onInput={(e: any) => {
                                const value = parseFloat(e.target.value);
                                if (value < 0.1 || value > 99) {
                                    e.preventDefault();
                                    e.target.value = value < 0.1 ? 0.1 : 99;
                                    return;
                                }
                                setSlippage(value);
                                localStorage.setItem('slippage', value.toString());
                            }}
                        />
                    </Box>
                </DialogContent>
            </Dialog>
        </PageBox>
    );
};